import React, { useEffect, useMemo, useState } from 'react';
import { fetchAudit } from '../services/demoExtrasService';

const ACTION_COLORS = {
  LOGIN:           '#16a34a',
  QUERY:           '#2563eb',
  EXPORT:          '#ea580c',
  IMPORT:          '#0891b2',
  ANALYSIS_SAVED:  '#7c3aed',
  ANALYSIS_LOADED: '#7c3aed',
  ANNOTATION:      '#db2777',
  LAYER_PUBLISHED: '#0ea5e9',
  ROLE_SWITCH:     '#64748b',
  ACCESS_DENIED:   '#dc2626'
};

function fmtTs(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return iso; }
}

export default function AuditPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [filterAction, setFilterAction] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchAudit({ limit: 500 });
      setRows(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const users = useMemo(() => Array.from(new Set(rows.map((r) => r.user))).sort(), [rows]);
  const actions = useMemo(() => Array.from(new Set(rows.map((r) => r.action))).sort(), [rows]);

  const filtered = rows.filter((r) =>
    (!filterUser || r.user === filterUser) && (!filterAction || r.action === filterAction)
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#0b1220', color: '#e2e8f0', overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(148,163,184,0.15)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Audit Trail</div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{filtered.length} of {rows.length} events · auto-refreshing</div>
        <div style={{ flex: 1 }} />
        <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} style={selectStyle}>
          <option value="">All users</option>
          {users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <select value={filterAction} onChange={(e) => setFilterAction(e.target.value)} style={selectStyle}>
          <option value="">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <button onClick={load} style={btnStyle}>Refresh</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {loading && rows.length === 0 ? (
          <div style={{ padding: 24, color: '#94a3b8' }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, color: '#94a3b8' }}>No audit events match filter.</div>
        ) : (
          <div>
            {filtered.map((r) => (
              <div key={r.id} style={{ borderTop: '1px solid rgba(148,163,184,0.08)', padding: '10px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, background: (ACTION_COLORS[r.action] || '#64748b') + '33', color: ACTION_COLORS[r.action] || '#94a3b8' }}>
                    {r.action}
                  </span>
                  <span style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500 }}>{r.user}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>· {r.role}</span>
                  <span style={{ fontSize: 11, color: '#64748b', marginLeft: 'auto', whiteSpace: 'nowrap' }}>{fmtTs(r.ts)}</span>
                </div>
                {r.detail && (
                  <div style={{ fontSize: 12, color: '#cbd5e1', marginBottom: 4, lineHeight: 1.4 }}>{r.detail}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#94a3b8', flexWrap: 'wrap' }}>
                  {(r.sources || []).map((s, i) => (
                    <span key={i} style={{ display: 'inline-block', padding: '1px 6px', background: 'rgba(148,163,184,0.12)', borderRadius: 3 }}>{s}</span>
                  ))}
                  {r.result_count > 0 && <span style={{ marginLeft: 'auto' }}>{r.result_count} records</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(148,163,184,0.15)', fontSize: 11, color: '#64748b' }}>
        Append-only · per-tenant SQLite · tamper-evident for compliance review
      </div>
    </div>
  );
}

const selectStyle = { background: 'rgba(15,23,42,0.6)', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 4, padding: '4px 8px', fontSize: 11 };
const btnStyle = { background: '#1e293b', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 4, padding: '4px 12px', fontSize: 11, cursor: 'pointer' };
